import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Menu,
  X,
  PlayCircle,
  FileText,
  CheckCircle2,
  Download,
  Maximize2,
  ZoomIn,
  ZoomOut,
  Search,
  ArrowLeft,
  BookOpen,
  Globe,
  Clock,
  ExternalLink,
  Headphones,
  Package,
  Lock,
  Bookmark,
  Bookmark as BookmarkIcon,
  TrendingUp,
  Award,
  PanelLeftClose
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { ScrollArea } from "../components/ui/scroll-area";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "../components/ui/accordion";
import { ContentViewer, ContentMetadata } from "../components/ContentViewers";
import { useCurriculumProgression } from "../hooks/useCurriculumProgression";
import { api } from "../services/api";
import bookmarkService from "../services/bookmarkService";
import testService from "../services/testService";
import assessmentService from "../services/assessmentService";
import TestItem from "../components/tests/TestItem";
import AssessmentItem from "../components/assessments/AssessmentItem";
import { toast } from "sonner";
import Swal from "sweetalert2";

const ContentReleaseBadge = ({ isReleased }) => {
  if (isReleased) return null;
  
  return (
    <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-600">
      <Clock className="h-3 w-3 mr-1" />
      Not Released
    </Badge>
  );
};

const ContentItem = React.memo(({ content, isActive, isLocked = false, lockReason = '', onClick, onContentDisplay, onMarkComplete, onMarkIncomplete, onToggleBookmark, bookmarkedContent, actualBookmarks, contentState, contentAssessments = [], onAssessmentClick, assessmentLockReason = '', onAssessmentLockedClick }) => {
  const getIcon = () => {
    switch (content.content_type) {
      case 'video':
        return PlayCircle;
      case 'file':
        return FileText;
      case 'quiz':
        return CheckCircle2;
      case 'urls':
        return Globe;
      case 'scorm':
        return Package;
      case 'audio':
        return Headphones;
      default:
        return FileText;
    }
  };

  const Icon = getIcon();
  const isBookmarked = actualBookmarks?.has(content.id);
  
  // Check if content is released for user's batch
  const isContentReleased = content?.is_released !== false; // Default to true if not specified
  const isLockedByRelease = !isContentReleased;
  
  const finalIsLocked = isLocked || isLockedByRelease;
  const finalLockReason = isLockedByRelease 
    ? 'This content has not been released for your batch yet.' 
    : lockReason;

  const handleClick = () => {
    if (finalIsLocked) {
      toast.info(finalLockReason);
      return;
    }
    onClick(content.id);
    if (onContentDisplay) {
      onContentDisplay(content);
    }
  };

  const handleMarkCompleteClick = (e, contentId) => {
    // Only show SweetAlert when clicking on the completion circle
    e.stopPropagation(); // Prevent the main handleClick from firing
    if (finalIsLocked) {
      toast.info(finalLockReason || 'Complete the previous content before marking this as completed.');
      return;
    }
    
    // Check multiple sources for completion status to ensure accuracy
    const isCompleted = content.status === "completed" || bookmarkedContent?.has(contentId);
    
    if (isCompleted) {
      // Show deselect confirmation dialog
      Swal.fire({
        text: "Do you want to mark this lesson as incomplete?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, mark as incomplete',
        cancelButtonText: 'Cancel',
        width: '25em'
      }).then((result) => {
        if (result.isConfirmed) {
          // Call the mark incomplete function
          onMarkIncomplete(contentId);
        }
      });
    } else {
      // Show completion confirmation dialog
      Swal.fire({
        text: "Do you want to mark lesson as completed?",
        icon: 'success',
        showCancelButton: true,
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
        confirmButtonText: 'Ok',
        cancelButtonText: 'Cancel',
        width: '25em'
      }).then((result) => {
        if (result.isConfirmed) {
          // Call the mark complete function
          onMarkComplete(contentId);
        }
      });
    }
  };

  const handleBookmarkClick = (e, contentId) => {
    e.stopPropagation(); // Prevent the main handleClick from firing
    // Use bookmark service to toggle bookmark
    const contentName = content?.title || content?.file_name || 'Content';
    console.log('Bookmark clicked:', { contentId, contentType: content.content_type, contentName });
    onToggleBookmark(contentId, content.content_type, contentName);
  };

  return (
    <>
      <div
        className={`group py-2 px-4 flex items-center gap-3 transition-colors ${isActive ? "bg-green-50/50" : !finalIsLocked ? "hover:bg-slate-50" : ""} ${finalIsLocked ? "cursor-not-allowed opacity-70" : "cursor-pointer"} w-full min-w-0`}
        onClick={handleClick}
      >
        <div className={`h-8 w-8 flex items-center justify-center rounded-full shrink-0 ${isActive ? "bg-green-600 text-white" : "bg-green-600/10 text-green-600"
          }`}>
          <Icon className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className={`text-[12px] font-medium truncate ${isActive ? "text-green-700" : "text-slate-600"}`}>
            {content?.title || content?.file_name || 'Content'}
          </p>
          <p className="text-[10px] text-slate-400 font-medium mt-0.5">
            {content.content_type === 'video' ? 'Video' :
              content.content_type === 'file' ? 'Document' :
                content.content_type === 'urls' ? 'URL' :
                  content.content_type === 'scorm' ? 'SCORM' :
                    content.content_type === 'audio' ? 'Audio' :
                      'Content'}
          </p>
        </div>
        <div className="shrink-0 ml-2 flex items-center gap-2">
          <button
            className={`transition-colors ${isBookmarked ? 'text-blue-500' : 'text-slate-400 hover:text-blue-500'}`}
            onClick={(e) => handleBookmarkClick(e, content.id)}
            title={isBookmarked ? 'Remove bookmark' : 'Add bookmark'}
          >
            {isBookmarked ? (
              <BookmarkIcon className="h-4 w-4 fill-current text-blue-500" />
            ) : (
              <Bookmark className="h-4 w-4" />
            )}
          </button>
          {(content.status === "completed" || bookmarkedContent?.has(content.id)) ? (
            <CheckCircle2 
              className="h-4 w-4 text-green-400 cursor-pointer hover:text-green-600 transition-colors" 
              onClick={(e) => handleMarkCompleteClick(e, content.id)}
            />
          ) : (
            <div
              className="h-4 w-4 rounded-full border-2 border-slate-200 cursor-pointer hover:border-green-400 transition-colors"
              onClick={(e) => handleMarkCompleteClick(e, content.id)}
            />
          )}
          {finalIsLocked && (
            <Lock className="h-4 w-4 text-red-500" />
          )}
          <ContentReleaseBadge isReleased={isContentReleased} />
        </div>
      </div>

      {/* Add assessments for this content item */}
      {contentAssessments && contentAssessments.length > 0 && (
        <div className="mt-2 w-full">
          <div className="text-xs font-semibold text-slate-500 mb-2">
            Assessments
          </div>
          <div className="space-y-2">
            {(contentAssessments || []).map(assessment => (
              <AssessmentItem
                key={assessment.id}
                assessment={assessment}
                onAssessmentClick={onAssessmentClick}
                locked={isLocked || !(contentState?.contentAssessment?.isUnlocked ?? false)}
                lockReason={isLocked ? lockReason : (contentState?.contentAssessment?.lockReason || 'Complete this content first')}
                onLockedClick={onAssessmentLockedClick}
              />
            ))}
          </div>
        </div>
      )}
    </>
  );
});

const TopicItem = React.memo(({ topic, topicState, contentStateMap, isActive, isExpanded, onToggle, activeContentId, onContentClick, onContentDisplay, onMarkComplete, onMarkIncomplete, onToggleBookmark, bookmarkedContent, actualBookmarks, topicTests = [], lesson_id, onTestClick, tests, assessments, onAssessmentClick }) => {
  const topicExamState = topicState?.topicExam ?? { isUnlocked: true, isCompleted: false, lockReason: '' };
  const isTopicUnlocked = topicState?.isUnlocked ?? true;
  const topicLockReason = topicState?.lockReason || 'Complete previous topics before unlocking this one.';
  const isTopicContentCompleted = topicState?.isContentCompleted ?? (topic.content?.some(content => bookmarkedContent.has(content.id)) || false);
  const isTopicExamCompleted = topicExamState?.isCompleted ?? (topicTests.some(test => test.isCompleted) || false);
  const isTopicFullyCompleted = topicState?.isFullyCompleted ?? (isTopicContentCompleted && isTopicExamCompleted);

  const topicHeaderClasses = `group py-2 px-4 flex items-center gap-3 transition-colors ${isActive
    ? "bg-orange-50/50"
    : isTopicUnlocked
      ? "hover:bg-slate-50"
      : ""
    } cursor-pointer ${isTopicUnlocked ? "" : "opacity-70"} w-full min-w-0`;

  const handleToggle = () => {
    onToggle();
  };

  return (
    <div className="min-w-0 w-full">
      <div
        className={topicHeaderClasses}
        onClick={handleToggle}
      >
        <div className={`h-8 w-8 flex items-center justify-center rounded-full shrink-0 ${isTopicFullyCompleted
          ? "bg-green-600 text-white"
          : isActive
            ? "bg-orange-600 text-white"
            : "bg-orange-600/10 text-orange-600"
          }`}>
          {isTopicFullyCompleted ? (
            <CheckCircle2 className="h-4 w-4" />
          ) : (
            <BookOpen className="h-4 w-4" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className={`text-[12px] font-semibold truncate flex-1 pr-2 ${isTopicFullyCompleted ? "text-green-700" : isActive ? "text-orange-700" : "text-slate-600"
              }`}>{topic?.name || 'Topic'}</p>
            <div className="flex items-center gap-2">
              {!isTopicUnlocked && (
                <Lock className="h-4 w-4 text-red-500" />
              )}
              <span className="text-[10px] text-slate-400 font-medium">
                {topic.content?.length || 0} items
              </span>
              {topicTests && topicTests.length > 0 && (
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${isTopicExamCompleted
                  ? "bg-green-100 text-green-600"
                  : isTopicContentCompleted
                    ? "bg-blue-100 text-blue-600"
                    : "bg-gray-100 text-gray-500"
                  }`}>
                  {topicTests.length} {topicTests.length === 1 ? 'Exam' : 'Exams'}
                </span>
              )}
              <ChevronRight className={`h-4 w-4 text-slate-400 shrink-0 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
            </div>
          </div>
        </div>
        <div className="shrink-0 ml-2">
          {isTopicFullyCompleted ? (
            <CheckCircle2 className="h-4 w-4 text-green-500" />
          ) : isTopicContentCompleted ? (
            <div className="h-5 w-5 rounded-full border-2 border-blue-200 flex items-center justify-center">
              <div className="h-2.5 w-2.5 rounded-full bg-blue-500 shadow-sm shadow-blue-500/50" />
            </div>
          ) : (
            <div className="h-4 w-4 rounded-full border-2 border-slate-200" />
          )}
        </div>
      </div>

      {isExpanded && topic.content && (
        <div className="w-full">
          {(topic.content || []).map((content) => {
            const contentState = contentStateMap?.get(content.id);
            const isContentUnlocked = (contentState?.isUnlocked ?? true) && isTopicUnlocked;
            const contentLockReason = !isTopicUnlocked
              ? topicLockReason
              : 'Complete the previous topic content to unlock this item.';
            return (
              <ContentItem
                key={content.id}
                content={content}
                isActive={content.id === activeContentId}
                onClick={onContentClick}
                isLocked={!isContentUnlocked}
                lockReason={contentLockReason}
                onContentDisplay={onContentDisplay}
                onMarkComplete={onMarkComplete}
                onMarkIncomplete={onMarkIncomplete}
                onToggleBookmark={onToggleBookmark}
                bookmarkedContent={bookmarkedContent}
                actualBookmarks={actualBookmarks}
                contentState={contentState}
                contentAssessments={assessments?.contents[content.id] || []}
                onAssessmentClick={onAssessmentClick}
                assessmentLockReason={contentLockReason}
                onAssessmentLockedClick={(reason) => toast.info(reason || 'Complete content to unlock this assessment.')}
              />
            );
          })}

          {/* Add content tests for each content item */}
          {(topic.content || []).map((content) => {
            const contentState = contentStateMap?.get(content.id);
            const contentTestState = contentState?.contentTest;
            const isContentTestUnlocked = (contentTestState?.isUnlocked ?? false) && isTopicUnlocked;
            const contentTestLockReason = !isTopicUnlocked
              ? topicLockReason
              : contentTestState?.lockReason || 'Complete the content to unlock this test.';

            return (
              <div key={`content-items-${content.id}`}>
                {tests?.contentTests[content.id]?.length > 0 && (
                  <div className="mt-2 w-full">
                    <div className="text-xs font-semibold text-slate-500 mb-2">
                      Tests
                    </div>
                    {(tests.contentTests[content.id] || []).map(test => (
                      <TestItem
                        key={test.id}
                        test={test}
                        onTestClick={onTestClick}
                        locked={!isContentTestUnlocked && !test.isCompleted}
                        lockReason={contentTestLockReason}
                        onLockedClick={(reason) => toast.info(reason || 'Complete the content to unlock this test.')}
                      />
                    ))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Add tests for this topic */}
          {topicTests && topicTests.length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Tests</div>
              {(topicTests || []).map(test => (
                <TestItem
                  key={test.id}
                  test={test}
                  onTestClick={onTestClick}
                  locked={!topicExamState?.isUnlocked && !test.isCompleted}
                  lockReason={topicExamState?.lockReason}
                  onLockedClick={(reason) => toast.info(reason || 'Complete the topic content to unlock this exam.')}
                />
              ))}
            </div>
          )}

          {/* Add assessments for this topic */}
          {assessments?.topics[topic.id] && assessments.topics[topic.id].length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Assessments</div>
              <div className="space-y-2">
                {(assessments.topics[topic.id] || []).map(assessment => (
                  <AssessmentItem
                    key={assessment.id}
                    assessment={assessment}
                    onAssessmentClick={onAssessmentClick}
                    locked={!isTopicUnlocked || !(topicState?.topicAssessment?.isUnlocked ?? false)}
                    lockReason={!isTopicUnlocked ? topicLockReason : (topicState?.topicAssessment?.lockReason || 'Complete topic content first')}
                    onLockedClick={(reason) => toast.info(reason || 'Complete the topic content to unlock this assessment.')}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const LessonItem = React.memo(({ lesson, lessonState, topicStateMap, contentStateMap, isActive, isExpanded, onToggle, expandedTopics, expandedTopicContents, onTopicToggle, onContentClick, onContentDisplay, onMarkComplete, onMarkIncomplete, onToggleBookmark, bookmarkedContent, actualBookmarks, tests, onTestClick, assessments, onAssessmentClick }) => {
  const Icon = lesson.type === "video" ? PlayCircle : FileText;
  const lessonExamState = lessonState?.lessonExam ?? { isUnlocked: true, isCompleted: false, lockReason: '' };
  const isLessonUnlocked = lessonState?.isUnlocked ?? true;
  const lessonLockReason = lessonState?.lockReason || 'Complete this lesson after finishing the previous one.';
  const isLessonCompleted = lessonState?.isCompleted ?? (lesson.topics?.every(topic =>
    topic.content?.some(content => bookmarkedContent.has(content.id)) || false
  ) && (tests.lessonTests[lesson.id]?.some(test => test.isCompleted) || false));
  const lessonHeaderClasses = `group py-3 px-4 flex items-center gap-3 transition-colors ${isActive
    ? "bg-blue-50/50"
    : isLessonUnlocked
      ? "hover:bg-slate-50"
      : ""
    } cursor-pointer ${isLessonUnlocked ? "" : "opacity-70"} w-full min-w-0`;
  const handleToggle = () => {
    onToggle(lesson.id);
  };

  return (
    <div className="min-w-0 w-full">
      <div
        className={lessonHeaderClasses}
        onClick={handleToggle}
      >
        <div className={`h-10 w-10 flex items-center justify-center rounded-full shrink-0 ${isLessonCompleted
          ? "bg-green-600 text-white"
          : isActive
            ? "bg-blue-600 text-white"
            : "bg-blue-600/10 text-blue-600"
          }`}>
          {isLessonCompleted ? (
            <CheckCircle2 className="h-5 w-5" />
          ) : (
            <Icon className="h-5 w-5" />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <p className={`text-[13px] font-bold truncate flex-1 pr-2 ${isLessonCompleted ? "text-green-700" : isActive ? "text-blue-700" : "text-slate-700"
              }`}>{lesson?.title || lesson?.name || 'Lesson'}</p>
            <div className="flex items-center gap-2">
              {!isLessonUnlocked && (
                <Lock className="h-4 w-4 text-red-500" />
              )}
              {lesson.topics && lesson.topics.length > 0 && (
                <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
              )}
              {tests.lessonTests[lesson.id] && tests.lessonTests[lesson.id].length > 0 && (
                <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full shrink-0 ${tests.lessonTests[lesson.id].some(test => test.isCompleted)
                  ? "bg-green-100 text-green-600"
                  : "bg-blue-100 text-blue-600"
                  }`}>
                  Exam
                </span>
              )}
            </div>
          </div>
          <p className="text-[11px] text-slate-500 font-medium mt-0.5">
            {lesson.duration_minutes ? `${lesson.duration_minutes} min` : lesson.pages ? `${lesson.pages} pages` : 'Content'}
            {lesson.topics && lesson.topics.length > 0 && ` • ${lesson.topics.length} topics`}
          </p>
        </div>
        <div className="shrink-0 ml-2">
          {isLessonCompleted ? (
            <CheckCircle2 className="h-5 w-5 text-green-500" />
          ) : (
            <div className="h-6 w-6 rounded-full border-2 border-blue-200 flex items-center justify-center">
              <div className="h-2.5 w-2.5 rounded-full bg-blue-500 shadow-sm shadow-blue-500/50" />
            </div>
          )}
        </div>
      </div>

      {isExpanded && lesson.topics && (
        <div className="w-full">
          {(lesson.topics || []).map((topic) => (
            <TopicItem
              key={topic.id}
              topic={topic}
              topicState={topicStateMap?.get(topic.id)}
              contentStateMap={contentStateMap}
              isActive={false}
              isExpanded={expandedTopics.has(topic.id)}
              onToggle={() => onTopicToggle(topic.id, lesson.id)}
              activeContentId={expandedTopicContents.get(topic.id)}
              onContentClick={(contentId) => onContentClick && onContentClick(topic.id, contentId)}
              onContentDisplay={onContentDisplay}
              onMarkComplete={onMarkComplete}
              onMarkIncomplete={onMarkIncomplete}
              onToggleBookmark={onToggleBookmark}
              bookmarkedContent={bookmarkedContent}
              actualBookmarks={actualBookmarks}
              topicTests={topic.id ? tests.topicTests[topic.id] : []}
              lesson_id={lesson.id}
              onTestClick={onTestClick}
              tests={tests}
              assessments={assessments}
              onAssessmentClick={onAssessmentClick}
            />
          ))}

          {/* Add tests for this lesson */}
          {tests?.lessonTests[lesson.id] && tests.lessonTests[lesson.id].length > 0 && (
            <div className="mt-4 pt-4 border-t border-slate-200">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Tests</div>
              {(tests.lessonTests[lesson.id] || []).map(test => (
                <TestItem
                  key={test.id}
                  test={test}
                  onTestClick={onTestClick}
                  locked={!lessonExamState?.isUnlocked && !test.isCompleted}
                  lockReason={lessonExamState?.lockReason}
                  onLockedClick={(reason) => toast.info(reason || 'Finish all topics to unlock the lesson exam.')}
                />
              ))}
            </div>
          )}

          {/* Add assessments for this lesson */}
          {assessments?.lessons[lesson.id] && assessments.lessons[lesson.id].length > 0 && (
            <div className=" px-4 border-t border-slate-200">
              <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Assessments</div>
              <div className="space-y-2">
                {(assessments.lessons[lesson.id] || []).map(assessment => (
                  <AssessmentItem
                    key={assessment.id}
                    assessment={assessment}
                    onAssessmentClick={onAssessmentClick}
                    locked={!isLessonUnlocked || !(lessonState?.lessonAssessment?.isUnlocked ?? false)}
                    lockReason={!isLessonUnlocked ? lessonLockReason : (lessonState?.lessonAssessment?.lockReason || 'Complete lesson content first')}
                    onLockedClick={(reason) => toast.info(reason || 'Complete the lesson content to unlock this assessment.')}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
});

const ContinueCourseSkeleton = () => (
  <div className="h-screen bg-slate-50/50 flex flex-col overflow-hidden animate-pulse">
    {/* Header Skeleton */}
    <header className="bg-white border-b border-slate-100 px-6 py-4 flex items-center justify-between">
      <div className="flex items-center gap-4">
        <div className="h-9 w-9 bg-slate-200 rounded-lg" />
        <div className="space-y-2">
          <div className="h-3 w-32 bg-slate-200 rounded" />
          <div className="h-2 w-20 bg-slate-100 rounded" />
        </div>
      </div>
      <div className="h-6 w-32 bg-slate-100 rounded-full" />
    </header>

    <div className="flex-1 flex flex-row-reverse overflow-hidden">
      {/* Main Content Skeleton */}
      <div className="flex-1 p-8 space-y-8">
        <div className="aspect-video w-full bg-slate-200 rounded-3xl" />
        <div className="space-y-4">
          <div className="h-8 w-1/2 bg-slate-200 rounded-xl" />
          <div className="h-4 w-3/4 bg-slate-100 rounded-lg" />
          <div className="h-4 w-2/3 bg-slate-100 rounded-lg" />
        </div>
      </div>

      {/* Sidebar Skeleton */}
      <aside className="w-[500px] border-r border-slate-200 bg-white hidden lg:block">
        <div className="p-8 border-b border-slate-100 space-y-4">
          <div className="h-6 w-32 bg-slate-200 rounded" />
          <div className="flex gap-4">
            <div className="h-4 w-20 bg-slate-100 rounded" />
            <div className="h-4 w-20 bg-slate-100 rounded" />
          </div>
        </div>
        <div className="p-4 space-y-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-16 w-full bg-slate-50 rounded-xl border border-slate-100" />
          ))}
        </div>
      </aside>
    </div>
  </div>
);

const ContinueCourse = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const { courseSlug } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [courseId, setCourseId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeLessonId, setActiveLessonId] = useState(null);
  const [expandedModules, setExpandedModules] = useState(new Set()); // Will be set based on released content
  const [expandedLessons, setExpandedLessons] = useState(new Set()); // Will be populated dynamically
  const [expandedTopics, setExpandedTopics] = useState(new Set()); // Will be populated dynamically
  const [activeContent, setActiveContent] = useState(null); // First content will be set dynamically
  const [activeContentType, setActiveContentType] = useState(null);
  const [bookmarkedContent, setBookmarkedContent] = useState(new Set()); // Used for completion tracking
  const [actualBookmarks, setActualBookmarks] = useState(new Set()); // Used for actual bookmarks
  const [assessments, setAssessments] = useState({
    modules: {},
    lessons: {},
    topics: {},
    contents: {}
  });
  const [loadingAssessments, setLoadingAssessments] = useState({
    modules: new Set(),
    lessons: new Set(),
    topics: new Set(),
    contents: new Set()
  });
  const curriculum = course?.modules || [];
  const getTopicLessonMap = useCallback(() => {
    const map = {};
    curriculum?.forEach(module => {
      module.lessons?.forEach(lesson => {
        lesson.topics?.forEach(topic => {
          if (topic?.id != null) {
            map[topic.id] = lesson.id;
          }
        });
      });
    });
    return map;
  }, [curriculum]);
  // Test-related state
  const [tests, setTests] = useState({
    moduleTests: {},    // Keyed by module_id: {moduleId: [tests]}
    lessonTests: {},    // Keyed by lesson_id: {lessonId: [tests]}
    topicTests: {},      // Keyed by topic_id: {topicId: [tests]}
    contentTests: {}     // Keyed by content_id: {contentId: [tests]}
  });
  const [loadingTests, setLoadingTests] = useState({
    modules: new Set(),
    lessons: new Set(),
    topics: new Set(),
    contents: new Set()
  });
  const [completionStatusUpdated, setCompletionStatusUpdated] = useState(false);
  const [resultAttemptIds, setResultAttemptIds] = useState(new Map());
  const progression = useCurriculumProgression({ course, tests, assessments });
  const {
    modules: progressionModules = [],
    moduleStateMap,
    lessonStateMap,
    topicStateMap,
    contentStateMap
  } = progression;

  // Refs to prevent duplicate calls
  const bookmarksLoaded = useRef(false);
  const loadedRef = useRef(false);

  const [expandedTopicContents, setExpandedTopicContents] = useState(new Map());
  const [completionPercentage, setCompletionPercentage] = useState(0);
  const [firstContentForAssessments, setFirstContentForAssessments] = useState(null);
  const totalContentCount = useMemo(() => {
    if (!course?.modules) return 0;
    return course.modules.reduce((total, module) => (
      total + (module.lessons || []).reduce((lessonTotal, lesson) => (
        lessonTotal + (lesson.topics || []).reduce((topicTotal, topic) => (
          topicTotal + (topic.content?.length || 0)
        ), 0)
      ), 0)
    ), 0);
  }, [course]);

  const updateCompletionPercentageFromSet = useCallback((nextCompletedSet) => {
    if (!totalContentCount) {
      setCompletionPercentage(0);
      return;
    }
    const nextPercentage = (nextCompletedSet.size / totalContentCount) * 100;
    setCompletionPercentage(Number(nextPercentage.toFixed(2)));
  }, [totalContentCount]);

  useEffect(() => {
    const loadUser = async () => {
      try {
        const res = await api.getMe();
        setCurrentUser(res.data);
      } catch (err) {
        console.error("Failed to load user data:", err);
        // Handle error, e.g., redirect to login or show a message
      }
    };

    loadUser();
  }, []);

  const getContentLockInfo = (contentId, topicId = null) => {
    if (!contentId) return { locked: false };

    const contentState = contentStateMap?.get(contentId);
    const moduleState = contentState?.moduleId ? moduleStateMap?.get(contentState.moduleId) : null;
    if (moduleState && !moduleState.isUnlocked) {
      return {
        locked: true,
        reason: moduleState.lockReason || 'Complete previous modules to unlock this one.'
      };
    }

    const lessonState = contentState?.lessonId ? lessonStateMap?.get(contentState.lessonId) : null;
    if (lessonState && !lessonState.isUnlocked) {
      return {
        locked: true,
        reason: lessonState.lockReason || 'Complete previous lessons to unlock this lesson.'
      };
    }

    const resolvedTopicId = contentState?.topicId || topicId;
    const topicState = resolvedTopicId ? topicStateMap?.get(resolvedTopicId) : null;
    if (topicState && !topicState.isUnlocked) {
      return {
        locked: true,
        reason: topicState.lockReason || 'Complete previous topics to unlock this topic.'
      };
    }

    if (contentState && !contentState.isUnlocked) {
      return {
        locked: true,
        reason: contentState.lockReason || 'Complete the previous content to unlock this item.'
      };
    }

    return { locked: false };
  };

  const handleContentDisplay = (content) => {
    if (!content) return;

    const lockInfo = getContentLockInfo(content.id);
    if (lockInfo.locked) return;

    setActiveContent(content);
    setActiveContentType(content.content_type);

    // Fetch content tests when content is displayed
    if (content.id) {
      fetchTestsForContent(content.id);
      // Fetch content assessments when content is displayed
      fetchAssessmentsForContent(content.id);
    }
  };

  const handleToggleBookmark = async (contentId, contentType, contentName) => {
    const wasBookmarked = actualBookmarks.has(contentId);
    setActualBookmarks(prev => {
      const next = new Set(prev);
      if (wasBookmarked) {
        next.delete(contentId);
      } else {
        next.add(contentId);
      }
      return next;
    });

    try {
      await bookmarkService.toggleBookmark(contentId, contentType, contentName);
    } catch (error) {
      // Roll back optimistic toggle on failure
      setActualBookmarks(prev => {
        const rollback = new Set(prev);
        if (wasBookmarked) {
          rollback.add(contentId);
        } else {
          rollback.delete(contentId);
        }
        return rollback;
      });
      console.error('Error toggling bookmark:', error);
    }
  };

  const updateCourseContentStatus = (contentId, status) => {
    setCourse((prevCourse) => {
      if (!prevCourse?.modules) return prevCourse;

      return {
        ...prevCourse,
        modules: (prevCourse.modules || []).map((module) => ({
          ...module,
          lessons: (module.lessons || []).map((lesson) => ({
            ...lesson,
            topics: (lesson.topics || []).map((topic) => ({
              ...topic,
              content: (topic.content || []).map((content) =>
                content.id === contentId ? { ...content, status } : content
              ),
            })),
          })),
        })),
      };
    });
  };

  const fetchBookmarks = async () => {
    if (bookmarksLoaded.current) return;
    if (!currentUser) {
      console.log('User not available yet, skipping fetchBookmarks');
      return;
    }

    bookmarksLoaded.current = true;

    try {
      const bookmarkedIds = await bookmarkService.getBookmarkedContentIds();
      setActualBookmarks(bookmarkedIds);
    } catch (error) {
      console.error('Error fetching bookmarks:', error);
    }
  };

  const handleMarkComplete = async (contentId) => {
    const wasCompleted = bookmarkedContent.has(contentId);
    if (!wasCompleted) {
      updateCourseContentStatus(contentId, 'completed');
      setBookmarkedContent(prev => {
        const next = new Set(prev);
        next.add(contentId);
        updateCompletionPercentageFromSet(next);
        return next;
      });
    }

    try {
      // Use currentUser instead of API call
      let userId = null;
      let instituteId = null;

      if (currentUser) {
        userId = currentUser.id || currentUser.user_id;
        instituteId = currentUser.institute_id || currentUser.instituteId;
      } else {
        // Fallback to localStorage if user not loaded yet
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        userId = user.id || user.user_id;
        instituteId = user.institute_id || user.instituteId;
      }

      if (!userId) {
        toast.error('User not found. Please login again.');
        return;
      }

      if (!instituteId) {
        toast.error('Institute not found. Please contact administrator.');
        return;
      }

      console.log('Marking content complete for user:', userId, 'institute:', instituteId, 'course:', courseId, 'content:', contentId);

      // Call progress API
      const response = await api.post('/progress/content', {
        user_id: userId,
        course_id: parseInt(courseId),
        content_id: contentId,
        institute_id: instituteId
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Content marked as complete!');

        // Show success message with SweetAlert
        Swal.fire({
          icon: 'success',
          title: 'Completed!',
          text: 'The lesson has been marked as completed.',
          timer: 2000,
          showConfirmButton: false,
          width: '20em'
        });
      } else {
        // Handle case where content is already completed
        if (response.data.message && response.data.message.includes('already marked as complete')) {
          toast.info(response.data.message);
        } else {
          if (!wasCompleted) {
            updateCourseContentStatus(contentId, 'incomplete');
            setBookmarkedContent(prev => {
              const rollback = new Set(prev);
              rollback.delete(contentId);
              updateCompletionPercentageFromSet(rollback);
              return rollback;
            });
          }
          toast.error(response.data.message || 'Failed to mark content as complete');
        }
      }
    } catch (error) {
      if (!wasCompleted) {
        updateCourseContentStatus(contentId, 'incomplete');
        setBookmarkedContent(prev => {
          const rollback = new Set(prev);
          rollback.delete(contentId);
          updateCompletionPercentageFromSet(rollback);
          return rollback;
        });
      }
      console.error('Error marking content complete:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication expired. Please login again.');
      } else {
        toast.error('Failed to mark content as complete');
      }
    }
  };

  const handleMarkIncomplete = async (contentId) => {
    const wasCompleted = bookmarkedContent.has(contentId);
    if (wasCompleted) {
      updateCourseContentStatus(contentId, 'incomplete');
      setBookmarkedContent(prev => {
        const next = new Set(prev);
        next.delete(contentId);
        updateCompletionPercentageFromSet(next);
        return next;
      });
    }

    try {
      // Use currentUser instead of API call
      let userId = null;
      let instituteId = null;

      if (currentUser) {
        userId = currentUser.id || currentUser.user_id;
        instituteId = currentUser.institute_id || currentUser.instituteId;
      } else {
        // Fallback to localStorage if user not loaded yet
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        userId = user.id || user.user_id;
        instituteId = user.institute_id || user.instituteId;
      }

      if (!userId) {
        toast.error('User not found. Please login again.');
        return;
      }

      if (!instituteId) {
        toast.error('Institute not found. Please contact administrator.');
        return;
      }

      console.log('Marking content incomplete for user:', userId, 'institute:', instituteId, 'course:', courseId, 'content:', contentId);

      // Call progress API to remove completion
      const response = await api.delete('/progress/content', {
        data: {
          user_id: userId,
          course_id: parseInt(courseId),
          content_id: contentId,
          institute_id: instituteId
        }
      });

      if (response.data.success) {
        toast.success(response.data.message || 'Content marked as incomplete!');

        // Show success message with SweetAlert
        Swal.fire({
          icon: 'success',
          title: 'Updated!',
          text: 'The lesson has been marked as incomplete.',
          timer: 2000,
          showConfirmButton: false,
          width: '20em'
        });
      } else {
        // Handle case where content is not marked as complete
        if (response.data.message && response.data.message.includes('not marked as complete')) {
          toast.info(response.data.message);
        } else {
          if (wasCompleted) {
            updateCourseContentStatus(contentId, 'completed');
            setBookmarkedContent(prev => {
              const rollback = new Set(prev);
              rollback.add(contentId);
              updateCompletionPercentageFromSet(rollback);
              return rollback;
            });
          }
          toast.error(response.data.message || 'Failed to mark content as incomplete');
        }
      }
    } catch (error) {
      if (wasCompleted) {
        updateCourseContentStatus(contentId, 'completed');
        setBookmarkedContent(prev => {
          const rollback = new Set(prev);
          rollback.add(contentId);
          updateCompletionPercentageFromSet(rollback);
          return rollback;
        });
      }
      console.error('Error marking content incomplete:', error);
      if (error.response?.status === 401) {
        toast.error('Authentication expired. Please login again.');
      } else {
        toast.error('Failed to mark content as incomplete');
      }
    }
  };

  const handleLessonToggle = (lessonId) => {
    const newExpanded = new Set(expandedLessons);
    if (newExpanded.has(lessonId)) {
      newExpanded.delete(lessonId);
    } else {
      newExpanded.add(lessonId);
      // Fetch tests when lesson is expanded
      fetchTestsForLesson(lessonId);
      // Fetch assessments when lesson is expanded
      fetchAssessmentsForLesson(lessonId);
    }
    setExpandedLessons(newExpanded);
    setActiveLessonId(lessonId);
  };

  const handleTopicToggle = (topicId, lessonId) => {
    const newExpanded = new Set(expandedTopics);
    if (newExpanded.has(topicId)) {
      newExpanded.delete(topicId);
    } else {
      newExpanded.add(topicId);
      // Fetch tests when topic is expanded
      if (topicId != null) {
        fetchTestsForTopic(topicId, lessonId);
        // Fetch assessments when topic is expanded
        fetchAssessmentsForTopic(topicId, lessonId);
      }
    }
    setExpandedTopics(newExpanded);
  };

  const handleContentClick = async (topicId, contentId) => {
    const lockInfo = getContentLockInfo(contentId, topicId);
    if (lockInfo.locked) {
      toast.info(lockInfo.reason);
      return;
    }

    let content = null;
    curriculum?.forEach(module => {
      module.lessons?.forEach(lesson => {
        lesson.topics?.forEach(topic => {
          if (topic.id === topicId) {
            content = topic.content?.find(c => c.id === contentId);
          }
        });
      });
    });

    if (!content) return;
    handleContentDisplay(content);
  };

  // Assessment fetching functions
  const fetchAssessmentsForModule = useCallback(async (moduleId) => {
    console.log('🔍 DEBUG: fetchAssessmentsForModule called with moduleId:', moduleId);
    console.log('🔍 DEBUG: loadingAssessments.modules.has(moduleId):', loadingAssessments.modules.has(moduleId));
    console.log('🔍 DEBUG: assessments.modules[moduleId]:', assessments.modules[moduleId]);
    console.log('🔍 DEBUG: currentUser:', currentUser);

    if (!moduleId || loadingAssessments.modules.has(moduleId) || (assessments.modules[moduleId] && assessments.modules[moduleId].length > 0)) {
      console.log('🔍 DEBUG: Returning early - already loading or fetched with data');
      return; // Already loading or already fetched with data
    }

    try {
      setLoadingAssessments(prev => ({
        ...prev,
        modules: new Set([...prev.modules, moduleId])
      }));

      if (!currentUser) {
        console.log('🔍 DEBUG: No currentUser, returning');
        return;
      }
      const userId = currentUser.id || currentUser.user_id;
      if (!userId) {
        console.log('🔍 DEBUG: No userId, returning');
        return;
      }

      console.log('🔍 DEBUG: About to call assessmentService.fetchModuleAssessments with:', { userId, courseId, moduleId });
      const moduleAssessments = await assessmentService.fetchModuleAssessments(userId, courseId, moduleId);
      console.log('🔍 DEBUG: Received moduleAssessments:', moduleAssessments);

      setAssessments(prev => ({
        ...prev,
        modules: {
          ...prev.modules,
          [moduleId]: moduleAssessments
        }
      }));
    } catch (error) {
      console.error('Error fetching module assessments:', error);
    } finally {
      setLoadingAssessments(prev => ({
        ...prev,
        modules: new Set([...prev.modules].filter(id => id !== moduleId))
      }));
    }
  }, [courseId, currentUser]);

  const fetchAssessmentsForLesson = useCallback(async (lessonId) => {
    if (!lessonId || loadingAssessments.lessons.has(lessonId) || (assessments.lessons[lessonId] && assessments.lessons[lessonId].length > 0)) {
      return; // Already loading or already fetched with data
    }

    try {
      setLoadingAssessments(prev => ({
        ...prev,
        lessons: new Set([...prev.lessons, lessonId])
      }));

      if (!currentUser) return;
      const userId = currentUser.id || currentUser.user_id;
      if (!userId) return;

      const lessonAssessments = await assessmentService.fetchLessonAssessments(userId, courseId, lessonId);

      setAssessments(prev => ({
        ...prev,
        lessons: {
          ...prev.lessons,
          [lessonId]: lessonAssessments
        }
      }));
    } catch (error) {
      console.error('Error fetching lesson assessments:', error);
    } finally {
      setLoadingAssessments(prev => ({
        ...prev,
        lessons: new Set([...prev.lessons].filter(id => id !== lessonId))
      }));
    }
  }, [courseId, currentUser]);

  const fetchAssessmentsForTopic = useCallback(async (topicId, lessonId) => {
    if (!topicId || !lessonId || loadingAssessments.topics.has(topicId) || (assessments.topics[topicId] && assessments.topics[topicId].length > 0)) {
      return; // Already loading or already fetched with data
    }

    try {
      setLoadingAssessments(prev => ({
        ...prev,
        topics: new Set([...prev.topics, topicId])
      }));

      if (!currentUser) return;
      const userId = currentUser.id || currentUser.user_id;
      if (!userId) return;

      const topicAssessments = await assessmentService.fetchTopicAssessments(userId, courseId, lessonId, topicId);

      setAssessments(prev => ({
        ...prev,
        topics: {
          ...prev.topics,
          [topicId]: topicAssessments
        }
      }));
    } catch (error) {
      console.error('Error fetching topic assessments:', error);
    } finally {
      setLoadingAssessments(prev => ({
        ...prev,
        topics: new Set([...prev.topics].filter(id => id !== topicId))
      }));
    }
  }, [courseId, currentUser]);

  const fetchAssessmentsForContent = useCallback(async (contentId) => {
    if (!contentId || loadingAssessments.contents.has(contentId) || (assessments.contents[contentId] && assessments.contents[contentId].length > 0)) {
      return; // Already loading or already fetched with data
    }

    try {
      setLoadingAssessments(prev => ({
        ...prev,
        contents: new Set([...prev.contents, contentId])
      }));

      if (!currentUser) return;
      const userId = currentUser.id || currentUser.user_id;
      if (!userId) return;

      const contentAssessments = await assessmentService.fetchContentAssessments(userId, courseId, contentId);

      setAssessments(prev => ({
        ...prev,
        contents: {
          ...prev.contents,
          [contentId]: contentAssessments
        }
      }));
    } catch (error) {
      console.error('Error fetching content assessments:', error);
    } finally {
      setLoadingAssessments(prev => ({
        ...prev,
        contents: new Set([...prev.contents].filter(id => id !== contentId))
      }));
    }
  }, [courseId, currentUser]);

  const handleAssessmentClick = (assessment) => {
    navigate(`/student/assessments/${assessment.id}`, {
      state: {
        assessmentId: assessment.id,
        fromCourse: true,
        from: 'continueCourse',
        assessmentType: assessment.assessmentType,
        courseId: courseId,
        courseSlug: courseSlug
      }
    });
  };

  // Dynamic test fetching functions using testService
  const fetchTestsForModule = useCallback(async (moduleId) => {
    if (loadingTests.modules.has(moduleId) || tests.moduleTests[moduleId]) {
      return; // Already loading or already fetched
    }

    try {
      setLoadingTests(prev => ({
        ...prev,
        modules: new Set([...prev.modules, moduleId])
      }));

      // Use currentUser instead of API call
      if (!currentUser) return;
      const userId = currentUser.id || currentUser.user_id;
      if (!userId) return;

      const assignments = await testService.fetchModuleTests(userId, courseId, moduleId);

      setTests(prev => ({
        ...prev,
        moduleTests: {
          ...prev.moduleTests,
          [moduleId]: assignments
        }
      }));

      // Don't call fetchCompletedAssignments here - it's handled by the useEffect

    } catch (error) {
      console.error('Error fetching module tests:', error);
    } finally {
      setLoadingTests(prev => ({
        ...prev,
        modules: new Set([...prev.modules].filter(id => id !== moduleId))
      }));
    }
  }, [courseId, loadingTests, tests, currentUser]);

  const handleModuleAccordionChange = useCallback((values) => {
    const newlyOpened = values.filter(value => !expandedModules.has(value));
    newlyOpened.forEach(value => {
      const match = value.match(/^module-(\d+)$/);
      if (!match) return;
      const moduleIdx = Number(match[1]);
      const module = curriculum[moduleIdx];
      if (module) {
        fetchTestsForModule(module.id);
        // Fetch assessments when module is expanded
        fetchAssessmentsForModule(module.id);
      }
    });
    setExpandedModules(new Set(values));
  }, [expandedModules, curriculum, fetchTestsForModule, fetchAssessmentsForModule]);

  const fetchTestsForLesson = useCallback(async (lessonId) => {
    if (loadingTests.lessons.has(lessonId) || tests.lessonTests[lessonId]) {
      return; // Already loading or already fetched
    }

    try {
      setLoadingTests(prev => ({
        ...prev,
        lessons: new Set([...prev.lessons, lessonId])
      }));

      // Use currentUser instead of API call
      if (!currentUser) return;
      const userId = currentUser.id || currentUser.user_id;
      if (!userId) return;

      const assignments = await testService.fetchLessonTests(userId, courseId, lessonId);

      setTests(prev => ({
        ...prev,
        lessonTests: {
          ...prev.lessonTests,
          [lessonId]: assignments
        }
      }));

      // Don't call fetchCompletedAssignments here - it's handled by the useEffect

    } catch (error) {
      console.error('Error fetching lesson tests:', error);
    } finally {
      setLoadingTests(prev => ({
        ...prev,
        lessons: new Set([...prev.lessons].filter(id => id !== lessonId))
      }));
    }
  }, [courseId, loadingTests, tests, currentUser]);

  const fetchTestsForTopic = useCallback(async (topicId, lessonId) => {
    if (topicId == null) {
      return;
    }
    if (loadingTests.topics.has(topicId) || tests.topicTests[topicId]) {
      return; // Already loading or already fetched
    }

    try {
      setLoadingTests(prev => ({
        ...prev,
        topics: new Set([...prev.topics, topicId])
      }));

      // Use currentUser instead of API call
      if (!currentUser) return;
      const userId = currentUser.id || currentUser.user_id;
      if (!userId) return;

      const assignments = await testService.fetchTopicTests(userId, courseId, lessonId, topicId);

      setTests(prev => ({
        ...prev,
        topicTests: {
          ...prev.topicTests,
          [topicId]: assignments
        }
      }));

      // Don't call fetchCompletedAssignments here - it's handled by the useEffect

      // 🔥 fetch all content exams under this topic
      const module = course?.modules?.find(m =>
        m.lessons?.some(l => l.id === lessonId)
      );
      const lesson = module?.lessons?.find(l => l.id === lessonId);
      const topic = lesson?.topics?.find(t => t.id === topicId);
      const contents = topic?.content || [];

      for (const content of contents) {
        if (!tests.contentTests[content.id]) {
          fetchTestsForContent(content.id);
        }
      }

    } catch (error) {
      console.error('Error fetching topic tests:', error);
    } finally {
      setLoadingTests(prev => ({
        ...prev,
        topics: new Set([...prev.topics].filter(id => id !== topicId))
      }));
    }
  }, [courseId, loadingTests, tests, currentUser]);

  const fetchTestsForContent = useCallback(async (contentId) => {
    if (!contentId) return;

    if (loadingTests.contents.has(contentId) || tests.contentTests[contentId]) {
      return;
    }

    try {
      setLoadingTests(prev => ({
        ...prev,
        contents: new Set([...prev.contents, contentId])
      }));

      if (!currentUser) return;

      const userId = currentUser.id || currentUser.user_id;

      const assignments = await testService.fetchContentTests(
        userId,
        courseId,
        contentId
      );

      setTests(prev => ({
        ...prev,
        contentTests: {
          ...prev.contentTests,
          [contentId]: assignments
        }
      }));

    } catch (error) {
      console.error("Error fetching content tests:", error);
    } finally {
      setLoadingTests(prev => ({
        ...prev,
        contents: new Set([...prev.contents].filter(id => id !== contentId))
      }));
    }
  }, [courseId, loadingTests, tests, currentUser]);

  const handleTestClick = (test) => {
    // Check if test is completed and has resultAttemptId
    if (test.isCompleted && test.resultAttemptId) {
      // Navigate to results page like LevelRoadmap does
      navigate(`/assignments/${courseId}/levels/${test.id}/results/${test.resultAttemptId}`, {
        state: {
          assignmentId: test.id,
          fromCourse: true,
          courseId: courseId,
          courseSlug: courseSlug,
          from: 'continueCourse'
        }
      });
      return;
    }

    // For incomplete tests, navigate to TestInstructions page
    navigate(`/assignments/${courseId}/levels/${test.id}/instructions`, {
      state: {
        assignmentId: test.id,
        levelData: {
          ...test,
          fromCourse: true,
          course_id: courseId,
          course_slug: courseSlug
        },
        courseId: courseId,
        courseSlug: courseSlug,
        from: 'continueCourse'
      }
    });
  };

  useEffect(() => {
    if (!curriculum.length) return;
    expandedModules.forEach(value => {
      const match = value.match(/^module-(\d+)$/);
      if (!match) return;
      const idx = Number(match[1]);
      const module = curriculum[idx];
      if (module) {
        fetchTestsForModule(module.id);
        // Fetch assessments for auto-expanded modules
        fetchAssessmentsForModule(module.id);
      }
    });
  }, [expandedModules, curriculum, fetchTestsForModule, fetchAssessmentsForModule]);

  useEffect(() => {
    expandedLessons.forEach(lessonId => {
      if (lessonId != null) {
        fetchTestsForLesson(lessonId);
        // Fetch assessments for auto-expanded lessons
        fetchAssessmentsForLesson(lessonId);
      }
    });
  }, [expandedLessons, fetchTestsForLesson, fetchAssessmentsForLesson]);

  useEffect(() => {
    expandedTopics.forEach(topicId => {
      const lessonId = getTopicLessonMap()[topicId];
      if (lessonId) {
        fetchTestsForTopic(topicId, lessonId);
        // Fetch assessments for auto-expanded topics
        fetchAssessmentsForTopic(topicId, lessonId);
      }
    });
  }, [expandedTopics, getTopicLessonMap, fetchTestsForTopic, fetchAssessmentsForTopic]);

  // Fetch content assessments for first content when user becomes available
  useEffect(() => {
    if (currentUser && firstContentForAssessments && firstContentForAssessments.id) {
      console.log('🔍 DEBUG: Fetching content assessments for first content:', firstContentForAssessments.id);
      fetchAssessmentsForContent(firstContentForAssessments.id);
      // Clear the first content reference to prevent duplicate calls
      setFirstContentForAssessments(null);
    }
  }, [currentUser, firstContentForAssessments, fetchAssessmentsForContent]);

  // Update completion status whenever tests are loaded
  useEffect(() => {
    const hasAnyTests = Object.keys(tests.moduleTests).length > 0 ||
      Object.keys(tests.lessonTests).length > 0 ||
      Object.keys(tests.topicTests).length > 0 ||
      Object.keys(tests.contentTests).length > 0;

    if (hasAnyTests && !completionStatusUpdated) {
      fetchCompletedAssignments();
    }
  }, [tests]);

  // Fetch completed assignments/tests status similar to LevelRoadmap
  const fetchCompletedAssignments = async () => {
    // Prevent multiple assignment API calls
    if (completionStatusUpdated) return;
    if (!currentUser) {
      console.log('User not available yet, skipping fetchCompletedAssignments');
      return;
    }

    try {
      const response = await api.getStudentAssignmentsDirect();
      const studentLessonData = response.data;

      // Process assignment completion status and result attempt IDs
      const completedAssignmentIds = new Set();
      const resultAttemptIds = new Map(); // Map assignmentId -> resultAttemptId

      if (studentLessonData.lessons) {
        studentLessonData.lessons.forEach(lesson => {
          if (lesson.assignments_by_level) {
            Object.values(lesson.assignments_by_level).forEach(assignments => {
              assignments.forEach(assignment => {
                const studentProgress = assignment.student_progress || {};
                const hasAttempt = (studentProgress.attempt_count || 0) > 0;

                if (hasAttempt) {
                  completedAssignmentIds.add(assignment.id);
                  // Store the result attempt ID if available
                  if (studentProgress.last_attempt_id) {
                    resultAttemptIds.set(assignment.id, studentProgress.last_attempt_id);
                  }
                }
              });
            });
          }
        });
      }

      console.log('Completed assignments:', Array.from(completedAssignmentIds));
      console.log('Result attempt IDs:', Array.from(resultAttemptIds.entries()));

      // Update tests with completion status and result attempt ID
      setTests(prevTests => {
        const updatedTests = { ...prevTests };

        // Update module tests
        Object.keys(updatedTests.moduleTests).forEach(moduleId => {
          updatedTests.moduleTests[moduleId] = updatedTests.moduleTests[moduleId].map(test => ({
            ...test,
            isCompleted: completedAssignmentIds.has(test.id),
            resultAttemptId: resultAttemptIds.get(test.id) || null
          }));
        });

        // Update lesson tests
        Object.keys(updatedTests.lessonTests).forEach(lessonId => {
          updatedTests.lessonTests[lessonId] = updatedTests.lessonTests[lessonId].map(test => ({
            ...test,
            isCompleted: completedAssignmentIds.has(test.id),
            resultAttemptId: resultAttemptIds.get(test.id) || null
          }));
        });

        // Update topic tests
        Object.keys(updatedTests.topicTests).forEach(topicId => {
          updatedTests.topicTests[topicId] = updatedTests.topicTests[topicId].map(test => ({
            ...test,
            isCompleted: completedAssignmentIds.has(test.id),
            resultAttemptId: resultAttemptIds.get(test.id) || null
          }));
        });

        // Update content tests
        Object.keys(updatedTests.contentTests).forEach(contentId => {
          updatedTests.contentTests[contentId] = updatedTests.contentTests[contentId].map(test => ({
            ...test,
            isCompleted: completedAssignmentIds.has(test.id),
            resultAttemptId: resultAttemptIds.get(test.id) || null
          }));
        });

        console.log('Updated tests with completion status:', updatedTests);
        return updatedTests;
      });

      // Mark that completion status has been updated
      setCompletionStatusUpdated(true);

    } catch (error) {
      console.error('Error fetching completed assignments:', error);
    }
  };

  const fetchCompletedContents = async () => {
    if (!courseId) {
      console.log('Course ID not available yet, skipping fetchCompletedContents');
      return;
    }
    try {
      const response = await api.get(`/progress/completed-contents/${courseId}`);
      const completedContentIds = response.data.completed_content_ids || [];
      const bookmarkedContentIds = response.data.bookmarked_content_ids || [];
      const totalContents = response.data.total_contents || 0;

      // Update course data with completion status
      setCourse(prevCourse => {
        if (!prevCourse) return prevCourse;

        const updatedCourse = { ...prevCourse };
        updatedCourse.modules = (updatedCourse.modules || []).map(module => ({
          ...module,
          lessons: (module.lessons || []).map(lesson => ({
            ...lesson,
            topics: lesson.topics ? lesson.topics.map(topic => ({
              ...topic,
              content: topic.content ? topic.content.map(content => ({
                ...content,
                status: completedContentIds.includes(content.id) ? 'completed' : content.status || 'incomplete'
              })) : topic.content
            })) : lesson.topics
          }))
        }));
        return updatedCourse;
      });

      // Set completed content IDs from API response (using bookmarkedContent state for completion tracking)
      setBookmarkedContent(new Set(completedContentIds));

      // Set completion percentage from API response
      const percentage = response.data.completion_percentage || 0;
      setCompletionPercentage(percentage);
      console.log(`Course completion: ${percentage}% (${completedContentIds.length}/${totalContents} items)`);

    } catch (error) {
      console.error('Error fetching completed contents:', error);
      // Don't show error toast for this, it's not critical
    }
  };

  const calculateCourseDuration = () => {
    if (!course?.modules) return { hours: 0, minutes: 0 };

    let totalMinutes = 0;
    course.modules.forEach(module => {
      if (module.lessons) {
        module.lessons.forEach(lesson => {
          if (lesson.duration_minutes) {
            totalMinutes += lesson.duration_minutes;
          }
        });
      }
    });

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return { hours, minutes };
  };

  const calculateModuleDuration = (module) => {
    if (!module?.lessons) return { hours: 0, minutes: 0 };

    let totalMinutes = 0;
    module.lessons.forEach(lesson => {
      if (lesson.duration_minutes) {
        totalMinutes += lesson.duration_minutes;
      }
    });

    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    return { hours, minutes };
  };

  const formatDuration = (hours, minutes) => {
    if (hours > 0 && minutes > 0) {
      return `${hours} Hr ${minutes} mins`;
    } else if (hours > 0) {
      return `${hours} Hr${hours > 1 ? 's' : ''}`;
    } else if (minutes > 0) {
      return `${minutes} mins`;
    }
    return 'Duration not available';
  };

  const handleLessonClick = (lessonId) => {
    setActiveLessonId(lessonId);
  };

  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    fetchCourseData();
  }, [courseSlug, courseId]);

  // Load completion data when courseId is available
  useEffect(() => {
    if (courseId && currentUser) {
      fetchCompletedContents();
      fetchBookmarks();
      fetchCompletedAssignments();
    }
  }, [courseId, currentUser]);

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/courses/by-slug/${courseSlug}`);
      setCourse(response.data);
      setCourseId(response.data.id); // Store the course ID for API calls
      setError(null);

      // Auto-expand immediately after course load - only for released content
      const modules = response.data.modules || [];
      if (modules.length > 0) {
        const firstModule = modules[0];
        
        // Find the first released content across all lessons and topics
        let firstReleasedContent = null;
        let targetLesson = null;
        let targetTopic = null;
        
        for (const lesson of firstModule.lessons || []) {
          for (const topic of lesson.topics || []) {
            const releasedContent = topic.content?.find(content => content.is_released !== false);
            if (releasedContent) {
              firstReleasedContent = releasedContent;
              targetLesson = lesson;
              targetTopic = topic;
              break;
            }
          }
          if (firstReleasedContent) break;
        }
        
        // Always expand first module so users can see the structure
        setExpandedModules(new Set(["module-0"]));

        // Only expand lesson/topic if there's released content
        if (firstReleasedContent && targetLesson && targetTopic) {
          setExpandedLessons(new Set([targetLesson.id]));
          setExpandedTopics(new Set([targetTopic.id]));

          handleContentDisplay(firstReleasedContent);
          setActiveContent(firstReleasedContent);
          setActiveContentType(firstReleasedContent.content_type);

          setExpandedTopicContents(
            new Map([[targetTopic.id, firstReleasedContent.id]])
          );

          // Store first released content for assessment fetching when user is available
          setFirstContentForAssessments(firstReleasedContent);

          // preload exams and assessments for auto expanded items
          if (firstModule) {
            fetchTestsForModule(firstModule.id);
            fetchAssessmentsForModule(firstModule.id);
          }

          if (targetLesson) {
            fetchTestsForLesson(targetLesson.id);
            fetchAssessmentsForLesson(targetLesson.id);
          }

          if (targetTopic) {
            fetchTestsForTopic(targetTopic.id, targetLesson.id);
            fetchAssessmentsForTopic(targetTopic.id, targetLesson.id);
          }

          if (firstReleasedContent) {
            fetchTestsForContent(firstReleasedContent.id);
            // Content assessments will be fetched when user is available via useEffect
          }
        } else {
          // No released content found - don't expand lesson/topic, but still show module structure
          console.log('No released content found in first module - keeping module collapsed except for structure');
        }
      }

      // API calls are now handled by useEffect when courseId is available

      // Set initial lesson after data is fetched
      const curriculum = response.data.modules || [];
      const initialLesson = curriculum.flatMap(m => m.lessons || []).find(l => l.status === "current") || (curriculum[0]?.lessons?.[0]);
      setActiveLessonId(initialLesson?.id);
    } catch (err) {
      console.error('Error fetching course data:', err);
      setError('Failed to load course');
      toast.error('Failed to load course');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <ContinueCourseSkeleton />;
  }

  if (error || !course) {
    return (
      <div className="h-screen bg-slate-50/50 flex flex-col items-center justify-center gap-4">
        <div className="text-red-500 text-lg font-semibold">{error || 'Course not found'}</div>
        <Button variant="outline" onClick={() => navigate("/courses")}>
          Back to Courses
        </Button>
      </div>
    );
  }

  // Transform API data to match expected structure
  const activeLesson = curriculum.flatMap(m => m.lessons || []).find(l => l.id === activeLessonId) || curriculum.flatMap(m => m.lessons || [])[0];

  return (
    <div className="h-screen bg-slate-50/50 flex flex-col overflow-hidden text-slate-900 font-sans relative">
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-100 px-4 md:px-6 py-3 md:py-4 flex items-center justify-between sticky top-0 z-[100] h-14 md:h-16">
        <div className="flex items-center gap-2 md:gap-4 min-w-0">
          <Button variant="ghost" size="icon" className="h-8 w-8 md:h-9 md:w-9 text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-colors shrink-0" onClick={() => setSidebarOpen(!sidebarOpen)}>
            {sidebarOpen ? <PanelLeftClose className="h-4 w-4 md:h-5 md:w-5" /> : <Menu className="h-4 w-4 md:h-5 md:w-5" />}
          </Button>

          <div className="flex flex-col min-w-0">
            <div className="flex items-center gap-1.5 md:gap-2 min-w-0">
              <Link to={`/courses`} className="p-1 hover:bg-slate-50 rounded-lg transition-colors shrink-0">
                <ArrowLeft className="h-3.5 w-3.5 text-slate-400" />
              </Link>
            <h1 className="inline-block font-bold text-[10px] md:text-xs uppercase tracking-widest text-primary bg-primary/10 px-2 py-1 rounded-md truncate max-w-[200px]">
  {course.title || 'Course'}
</h1>
              {/* <div className="h-1 w-1 bg-slate-200 rounded-full shrink-0" /> */}
              {/* <span className="text-[9px] md:text-[10px] font-black text-blue-600 uppercase tracking-wider shrink-0">
                M{curriculum.findIndex(m => m.lessons?.some(l => l.id === activeLesson?.id)) + 1}
              </span> */}
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2 md:gap-3 bg-slate-100/50 px-3 md:px-4 py-1 md:py-1.5 rounded-full border border-slate-100 shrink-0">
            <div className="hidden sm:block h-1.5 w-16 md:w-24 bg-slate-200 rounded-full overflow-hidden">
              <div className="h-full bg-blue-600 transition-all duration-500" style={{ width: `${completionPercentage}%` }} />
            </div>
            <span className="text-[9px] md:text-[10px] font-black text-slate-600 tracking-tighter sm:tracking-normal">{Math.round(completionPercentage)}% <span className="hidden sm:inline">Done</span></span>
          </div>
        </div>
      </header>

      <div className="flex-1 flex flex-row-reverse overflow-hidden relative">
        {/* Mobile Sidebar Overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm z-50 lg:hidden transition-opacity duration-300"
            onClick={() => setSidebarOpen(false)}
          />
        )}
        {/* Main Content Area */}
        <div className={`flex-1 overflow-hidden transition-all duration-300 relative`}>
          <div className="h-full flex flex-col">
            <ScrollArea className="flex-1">
              <div className={`p-4 md:p-8 mx-auto w-full`}>
                {activeContent ? (
                  <ContentViewer content={activeContent} contentType={activeContentType} />
                ) : activeLesson?.type === "video" ? (
                  <ContentViewer content={{ file_path: activeLesson?.videoUrl }} contentType="video" title={activeLesson?.title || activeLesson?.name || 'Lesson'} />
                ) : activeLesson ? (
                  <ContentViewer content={{}} contentType="file" title={activeLesson?.title || activeLesson?.name || 'Lesson'} />
                ) : (
                  <div className="text-center py-12">
                    <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-600 mb-2">No Course Content Available</h3>
                    <p className="text-sm text-slate-400">This course doesn't have any lessons,topics or content yet.
                      Please check back later or contact your admin/instructor.</p>
                  </div>
                )}

                {activeContent ? (
                  <ContentMetadata content={activeContent} contentType={activeContentType} />
                ) : (
                  <div className="mt-10 border-t border-slate-100 pt-10 grid lg:grid-cols-3 gap-10 mb-20">
                    <div className="lg:col-span-2 space-y-6">
                      <h2 className="text-2xl font-bold tracking-tight text-slate-800">{activeLesson?.title || activeLesson?.name || 'Lesson'}</h2>
                      <p className="text-base text-slate-500 leading-relaxed max-w-2xl font-medium">
                        This {activeLesson?.type || 'content'} is tailored to provide a deep understanding of the subject.
                        We focus on practical outcomes and industry standards to ensure you are job-ready from day one.
                      </p>
                      {/* <div className="flex gap-2">
                        {['Analysis', 'Best Practices', 'Implementation'].map(tag => (
                          <Badge key={tag} variant="secondary" className="bg-blue-50 text-blue-600 border-none font-bold text-[10px] px-3 py-1 uppercase tracking-widest">{tag}</Badge>
                        ))}
                      </div> */}
                    </div>
                    {/* <div className="bg-white border border-slate-100 rounded-2xl p-6 shadow-sm space-y-6">
                      <h3 className="text-[11px] font-bold uppercase text-slate-400 tracking-widest">Resources</h3>
                      <div className="space-y-4">
                        <div className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-xl cursor-pointer transition-colors group border border-transparent hover:border-slate-100">
                          <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Download className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-[13px] font-bold text-slate-800 line-clamp-1">{activeLesson?.title || activeLesson?.name || 'Lesson'} Resource</p>
                            <p className="text-[10px] text-slate-400 font-medium">
                              {activeLesson?.type === 'video' ? 'Video' : 'PDF'} • 42.8 KB
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-4 p-3 hover:bg-slate-50 rounded-xl cursor-pointer transition-colors border border-transparent hover:border-slate-100">
                          <div className="h-10 w-10 rounded-lg bg-blue-50 flex items-center justify-center">
                            <Globe className="h-5 w-5 text-blue-600" />
                          </div>
                          <div className="flex-1">
                            <p className="text-[13px] font-bold text-slate-800">Reference Doc</p>
                            <p className="text-[10px] text-slate-400 font-medium">Online Link</p>
                          </div>
                        </div>
                      </div>
                    </div> */}
                  </div>
                )}

                {/* Add content tests after the content */}
                {activeContent && tests?.contentTests[activeContent.id] && tests.contentTests[activeContent.id].length > 0 && (
                  <div className="mt-8 pt-8 border-t border-slate-200">
                    <div className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Content Tests</div>
                    <div className="space-y-3">
                      {(tests.contentTests[activeContent.id] || []).map(test => {
                        const contentState = contentStateMap?.get(activeContent.id);
                        const contentTestState = contentState?.contentTest;
                        const isContentTestUnlocked = contentTestState?.isUnlocked ?? false;
                        const contentTestLockReason = contentTestState?.lockReason || 'Complete the content to unlock this test.';

                        return (
                          <TestItem
                            key={test.id}
                            test={test}
                            onTestClick={handleTestClick}
                            locked={!isContentTestUnlocked}
                            lockReason={contentTestLockReason}
                            onLockedClick={(reason) => toast.info(reason || 'Complete the content to unlock this test.')}
                          />
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Add content assessments after the content */}
                {activeContent && assessments?.contents[activeContent.id] && assessments.contents[activeContent.id].length > 0 && (
                  <div className="mt-8 pt-8 border-t border-slate-200">
                    <div className="text-sm font-semibold text-slate-500 uppercase tracking-wider mb-4">Content Assessments</div>
                    <div className="space-y-3">
                      {(assessments.contents[activeContent.id] || []).map(assessment => (
                        <AssessmentItem
                          key={assessment.id}
                          assessment={assessment}
                          onAssessmentClick={handleAssessmentClick}
                          locked={false} // Content is already displayed, so assessments should be available
                          lockReason=""
                          onLockedClick={(reason) => toast.info(reason)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </ScrollArea>
          </div>
        </div>

        {/* Neat Curriculum Sidebar */}
        <aside className={`fixed lg:relative inset-y-0 left-0 z-[60] lg:z-10 bg-white border-r border-slate-200 transform transition-all duration-300 flex-shrink-0 overflow-x-hidden shadow-2xl lg:shadow-none ${sidebarOpen ? "translate-x-0 w-[85vw] sm:w-[320px] md:w-[400px] lg:w-[500px]" : "-translate-x-full w-0"}`}>
          <div className="flex flex-col h-full">
            <div className="px-6 md:px-8 py-5 md:py-6 border-b border-slate-100 mt-10 lg:mt-0">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-bold text-lg md:text-xl text-slate-800">Course Content</h3>
                <Button variant="ghost" size="icon" className="lg:hidden h-8 w-8 text-slate-400" onClick={() => setSidebarOpen(false)}>
                  <X className="h-5 w-5" />
                </Button>
              </div>
              <div className="flex items-center gap-5 text-slate-500">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-slate-400" />
                  <span className="text-xs font-bold">{curriculum.length} modules</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-slate-400" />
                  <span className="text-xs font-bold">{(() => {
                    const duration = calculateCourseDuration();
                    return formatDuration(duration.hours, duration.minutes);
                  })()}</span>
                </div>
                {/* Pages badge commented out as requested */}
                {/* <div className="flex items-center gap-2">
                  <BookOpen className="h-4 w-4 text-slate-400" />
                  <span className="text-xs font-bold">160 pages</span>
                </div> */}
              </div>
            </div>

            <div className="flex-1 bg-white/50 overflow-y-auto no-scrollbar">
              <div className="p-4">
                {curriculum.length === 0 ? (
                  <div className="text-center py-12">
                    <BookOpen className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-slate-600 mb-2">No modules are added</h3>
                    <p className="text-sm text-slate-400">This course doesn't have any modules, lessons, or topics yet.</p>
                  </div>
                ) : (
                  <Accordion type="multiple" value={Array.from(expandedModules)} onValueChange={handleModuleAccordionChange} className="space-y-4 w-full">
                    {curriculum.map((module, idx) => {
                      const moduleState = moduleStateMap?.get(module.id);
                      const moduleTestState = moduleState?.moduleTest ?? { isUnlocked: true, isCompleted: false, lockReason: '' };
                      const isModuleUnlocked = moduleState?.isUnlocked ?? true;
                      const isModuleActive = module.lessons?.some(l => l.id === activeLessonId);
                      const triggerClasses = `hover:no-underline px-6 py-4 transition-all cursor-pointer ${isModuleActive
                        ? "bg-blue-600 text-white"
                        : isModuleUnlocked
                          ? "bg-white hover:bg-slate-50 text-slate-800"
                          : "bg-slate-50 text-slate-400"
                        } ${isModuleUnlocked ? "" : "opacity-70"}`;
                      return (
                        <AccordionItem key={module.id} value={`module-${idx}`} className="border border-slate-200 rounded-xl overflow-hidden px-0 bg-white shadow-sm min-w-0">
                          <AccordionTrigger className={`${triggerClasses} w-full min-w-0`}>
                            <div className={`flex flex-col items-start gap-2.5 w-full text-left min-w-0 flex-1 overflow-hidden`}>
                              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1 sm:gap-4 w-full min-w-0 pr-2">
                                <span className={`text-[10px] md:text-[11px] font-black uppercase tracking-[0.15em] shrink-0 ${isModuleActive ? "text-blue-100" : "text-blue-600"}`}>Module {idx + 1}</span>
                                <h4 className={`text-[13px] md:text-sm font-black leading-tight truncate sm:text-right ${isModuleActive ? "text-white" : "text-slate-900"}`}>{module?.title || module?.name || 'Module'}</h4>
                              </div>
                              <div className="flex items-center gap-1.5 md:gap-2 min-w-0 flex-wrap">
                                {!isModuleUnlocked && (
                                  <Lock className="h-3.5 w-3.5 text-red-500 shrink-0" />
                                )}
                                <Badge variant="secondary" className={`bg-black/10 text-[9px] font-black px-2 py-0.5 border border-white/10 uppercase tracking-tighter shrink-0 ${isModuleActive ? "text-white border-white/20" : "bg-blue-50 text-blue-600 border-blue-100"}`}>
                                  {module.lessons?.length || 0} L
                                </Badge>
                                <Badge variant="secondary" className={`bg-black/10 text-[9px] font-black px-2 py-0.5 border border-white/20 uppercase tracking-tighter shrink-0 ${isModuleActive ? "text-white border-white/20" : "bg-pink-50 text-pink-600 border-pink-100"}`}>
                                  {(() => {
                                    const duration = calculateModuleDuration(module);
                                    return formatDuration(duration.hours, duration.minutes);
                                  })()}
                                </Badge>
                              </div>
                            </div>
                          </AccordionTrigger>
                          <AccordionContent className="p-0 overflow-hidden divide-y divide-slate-100/50">
                            {(module.lessons || []).map((lesson) => (
                              <LessonItem
                                key={lesson.id}
                                lesson={lesson}
                                lessonState={lessonStateMap?.get(lesson.id)}
                                topicStateMap={topicStateMap}
                                contentStateMap={contentStateMap}
                                isActive={lesson.id === activeLessonId}
                                isExpanded={expandedLessons.has(lesson.id)}
                                onToggle={handleLessonToggle}
                                expandedTopics={expandedTopics}
                                expandedTopicContents={expandedTopicContents}
                                onTopicToggle={handleTopicToggle}
                                onContentClick={handleContentClick}
                                onContentDisplay={handleContentDisplay}
                                onMarkComplete={handleMarkComplete}
                                onMarkIncomplete={handleMarkIncomplete}
                                onToggleBookmark={handleToggleBookmark}
                                bookmarkedContent={bookmarkedContent}
                                actualBookmarks={actualBookmarks}
                                tests={tests}
                                onTestClick={handleTestClick}
                                assessments={assessments}
                                onAssessmentClick={handleAssessmentClick}
                              />
                            ))}

                            {/* Add tests for this module */}
                            {tests?.moduleTests[module.id] && tests.moduleTests[module.id].length > 0 && (
                              <div className="mt-4 pt-4 border-t border-slate-200">
                                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Tests</div>
                                {tests.moduleTests[module.id].map(test => (
                                  <TestItem
                                    key={test.id}
                                    test={test}
                                    onTestClick={handleTestClick}
                                    locked={!moduleState?.moduleTest?.isUnlocked && !test.isCompleted}
                                    lockReason={moduleState?.moduleTest?.lockReason || moduleState?.lockReason}
                                    onLockedClick={(reason) => toast.info(reason || 'Complete all lessons to unlock the module exam.')}
                                  />
                                ))}
                              </div>
                            )}

                            {/* Add assessments for this module */}
                            {assessments?.modules[module.id] && assessments.modules[module.id].length > 0 && (
                              <div className="px-4 mb-3">
                                <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-3">Assessments</div>
                                <div className="space-y-2">
                                  {assessments.modules[module.id].map(assessment => (
                                    <AssessmentItem
                                      key={assessment.id}
                                      assessment={assessment}
                                      onAssessmentClick={handleAssessmentClick}
                                      locked={!isModuleUnlocked || !(moduleState?.moduleAssessment?.isUnlocked ?? false)}
                                      lockReason={!isModuleUnlocked ? (moduleState?.lockReason || 'Complete previous modules first') : (moduleState?.moduleAssessment?.lockReason || 'Complete module content first')}
                                      onLockedClick={(reason) => toast.info(reason || 'Complete the module content to unlock this assessment.')}
                                    />
                                  ))}
                                </div>
                              </div>
                            )}
                          </AccordionContent>
                        </AccordionItem>
                      );
                    })}
                  </Accordion>
                )}
              </div>
            </div>
          </div>
        </aside>

        {/* Subtle Toggle for Sidebar */}
        {!sidebarOpen && (
          <Button
            variant="default"
            size="icon"
            className="fixed bottom-8 right-8 h-12 w-12 rounded-full shadow-lg lg:flex hidden z-50 bg-blue-600 hover:bg-blue-700"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="h-6 w-6 text-white" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default ContinueCourse;
